import React from 'react';
import styled from 'styled-components';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import MediaPreview from '../../../components/media-preview/MediaPreview';
import ToolTip from '../../../components/tooltip/ToolTip';

/**
 * Preview 左サイドバーのメディアサムネイル。
 * - `SortableSidebarThumb`（default export）: dnd-kit の並び替え対応版
 * - `StaticSidebarThumb`: 並び替え権限がないユーザー向けの閲覧専用版
 * 見た目（サイズ・ツールチップ・候補数バッジ）は両者で共通。
 */

// ===== Styled Components =====

/**
 * 比較グループ（候補 _a/_b ..）を1アイコンにまとめて表示する際の候補数バッジ。
 * ショット編集リストの行数とサイドバーのアイコン数が一致しないため、
 * 「このアイコンは複数候補を含む」ことを明示して見落としを防ぐ。
 * 通常の通知バッジより控えめにするため、background 地 + border 枠 +
 * light-gray 文字の淡い配色にしている。
 */
const ThumbBadge = styled.span.attrs({ className: 'bodytext-11' })`
  position: absolute;
  top: -4px;
  right: -4px;
  z-index: 2;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: var(--soremo-background);
  border: 1px solid var(--soremo-border);
  color: var(--soremo-light-gray);
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  pointer-events: none;
`;

const Thumb = styled.div<{ $isActive: boolean; $isDragging: boolean; $size: number }>`
  width: ${p => p.$size}px;
  height: ${p => p.$size}px;
  border-radius: 8px;
  overflow: hidden;
  cursor: ${p => (p.$isDragging ? 'grabbing' : 'pointer')};
  flex-shrink: 0;
  border: 2px solid ${p => (p.$isActive ? 'var(--soremo-blue)' : 'transparent')};
  transition: border-color 0.2s, transform 0.2s, box-shadow 0.2s;
  opacity: ${p => (p.$isDragging ? 0.6 : 1)};
  touch-action: none;

  &:hover {
    border-color: var(--soremo-blue);
    transform: scale(1.05);
  }

  &:focus-within {
    border-color: var(--soremo-blue);
    box-shadow: 0 0 0 4px rgba(0, 154, 206, 0.25);
  }
`;

// ===== Props =====

export interface SidebarThumbViewProps {
  isActive: boolean;
  tooltipText: string;
  thumbnailSrc: string;
  onClick: () => void;
  mediaPreviewProps: Record<string, unknown>;
  /** サムネイル一辺のサイズ（px）。ビューポート超過時に縮小される */
  size?: number;
  /** 比較グループの候補数。2以上で右上にバッジ表示（単体スライドは 0 / 省略） */
  badgeCount?: number;
}

export interface SortableSidebarThumbProps extends SidebarThumbViewProps {
  /** dnd-kit の sortable id */
  id: string;
}

// ===== 共通ビュー =====

/** ツールチップ付きサムネイル + 候補数バッジ（Sortable / Static 共通の中身） */
const ThumbView: React.FC<SidebarThumbViewProps & { isDragging?: boolean }> = ({
  isActive,
  tooltipText,
  thumbnailSrc,
  onClick,
  mediaPreviewProps,
  size = 56,
  badgeCount = 0,
  isDragging = false,
}) => (
  <>
    <ToolTip description={tooltipText} placement="right" theme="sidebar-thumb">
      <Thumb
        $isActive={isActive}
        $isDragging={isDragging}
        $size={size}
        onClick={isDragging ? undefined : onClick}
      >
        <MediaPreview
          src={thumbnailSrc}
          sizeMode={{ mode: 'fixed-height' as const, height: `${size}px` }}
          style={{ pointerEvents: 'none' }}
          {...mediaPreviewProps}
        />
      </Thumb>
    </ToolTip>
    {badgeCount >= 2 && <ThumbBadge>{badgeCount}</ThumbBadge>}
  </>
);

// ===== Components =====

/** 閲覧専用版（並び替え権限がないユーザー向け） */
export const StaticSidebarThumb: React.FC<SidebarThumbViewProps> = props => (
  // バッジを Thumb（overflow: hidden）の外にはみ出して表示するための基準要素
  <div style={{ position: 'relative' }}>
    <ThumbView {...props} />
  </div>
);

/** 並び替え（dnd-kit sortable）対応版 */
const SortableSidebarThumb: React.FC<SortableSidebarThumbProps> = ({ id, ...viewProps }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    outline: 'none',
    // バッジを Thumb（overflow: hidden）の外にはみ出して表示するため
    position: 'relative',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <ThumbView {...viewProps} isDragging={isDragging} />
    </div>
  );
};

export default SortableSidebarThumb;
